import { TYPE } from "@gaoya/shared";
import { C, drawPanel, fillRound } from "../theme.js";
import {
  HONOR_LADDER_H,
  HONOR_WINS_COL,
  drawHonorLadder,
  drawNameWithHonor,
  drawWinsCol,
  honorPreviewActive,
  honorPreviewRows,
} from "../widgets/honor.js";
import { drawScrim } from "./settings.js";
import type { UiState } from "../../game/state.js";

const MEDAL: [string, string][] = [
  ["#E4B95F", "#6B4A12"],
  ["#D9D4CB", "#4E4740"],
  ["#E2B48C", "#6A3E1E"],
];

type R = { x: number; y: number; w: number; h: number };

export function leaderboardLayout(
  w: number,
  h: number,
  ui?: Pick<UiState, "safeTop" | "safeBottom">,
): { rank: R; ladder: R } {
  const closeY = Math.min(h * 0.86, h - (ui?.safeBottom ?? 0) - 72);
  const x = 24;
  const inner = w - 48;
  const top = Math.max(h * 0.08, (ui?.safeTop ?? 0) + 40);
  const ladderH = Math.min(HONOR_LADDER_H, Math.max(156, closeY - 12 - top - 8 - 210));
  const ladder: R = { x, y: closeY - 10 - ladderH, w: inner, h: ladderH };
  const rank: R = { x, y: top, w: inner, h: Math.max(200, ladder.y - 8 - top) };
  return { rank, ladder };
}

/** 与 Session.fetchLeaderboard 中开放数据域面板同尺寸，避免好友榜被拉伸。 */
export function leaderboardPanel(w: number, h: number, ui?: Pick<UiState, "safeTop" | "safeBottom">): R {
  return leaderboardLayout(w, h, ui).rank;
}

export function drawLeaderboard(ctx: CanvasRenderingContext2D, ui: UiState, w: number, h: number): void {
  drawScrim(ctx, w, h, 0.55);
  const g = leaderboardLayout(w, h, ui);
  if (ui.friendRankCanvas) {
    ctx.drawImage(ui.friendRankCanvas, g.rank.x, g.rank.y, g.rank.w, g.rank.h);
  } else {
    drawRankCard(ctx, ui, w, g.rank);
  }
  drawHonorLadder(ctx, g.ladder.x, g.ladder.y, g.ladder.w, g.ladder.h, ui.totalWins);
}

function drawRankCard(ctx: CanvasRenderingContext2D, ui: UiState, w: number, p: R): void {
  const rows = honorPreviewActive() ? honorPreviewRows() : ui.leaderboard.slice(0, 10);
  const rowH = 44;
  drawPanel(ctx, p.x, p.y, p.w, p.h, 20, C.card, null, null);
  fillRound(ctx, p.x, p.y, p.w, 84, 20, C.goldSoft);
  ctx.fillStyle = C.goldSoft;
  ctx.fillRect(p.x, p.y + 60, p.w, 24);
  ctx.fillStyle = C.ink;
  ctx.font = `700 22px ${TYPE.serif}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("好友对战榜", w / 2, p.y + 34);
  ctx.fillStyle = C.goldDeep;
  ctx.font = `12px ${TYPE.sans}`;
  ctx.fillText(ui.leaderboardHint || "同玩好友按胜场排序", w / 2, p.y + 60);
  if (rows.length === 0) {
    ctx.fillStyle = C.ink3;
    ctx.font = `13px ${TYPE.sans}`;
    ctx.fillText("暂无上榜好友，去对战一局吧", w / 2, p.y + 84 + 36);
    return;
  }
  const listBottom = p.y + p.h - 8;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!row) continue;
    const ry = p.y + 92 + i * rowH;
    if (ry + rowH > listBottom) break;
    const mine = row.nickname === ui.nickname;
    if (mine) fillRound(ctx, p.x + 8, ry + 2, p.w - 16, rowH - 4, 10, "rgba(228,185,95,0.2)");
    else if (i > 0) {
      ctx.fillStyle = C.line;
      ctx.fillRect(p.x + 16, ry, p.w - 32, 1);
    }
    const cy = ry + rowH / 2;
    const medal = MEDAL[i];
    if (medal) {
      fillRound(ctx, p.x + 18, cy - 12, 24, 24, 12, medal[0]);
      ctx.fillStyle = medal[1];
    } else ctx.fillStyle = C.ink3;
    ctx.font = `700 12px ${TYPE.sans}`;
    ctx.textAlign = "center";
    ctx.fillText(String(i + 1), p.x + 30, cy + 0.5);
    drawWinsCol(ctx, row.wins, p.x + p.w - 18, cy, 16);
    ctx.textAlign = "left";
    ctx.fillStyle = C.ink;
    ctx.font = `${mine ? 700 : 500} 15px ${TYPE.sans}`;
    drawNameWithHonor(ctx, row.nickname + (mine ? "（我）" : ""), row.wins, p.x + 54, cy, p.x + p.w - 18 - HONOR_WINS_COL);
  }
}
